import type { AcceptableValue, SelectGroup } from "reka-ui";

export type ObjectSelect = {
	text: string;
	value: string;
	disabled?: boolean;
};

type NotDefined = null | undefined;

export type SelectItem<T extends AcceptableValue> = {
	type: "item";
	value: string | number;
	text: string;
	disabled: boolean;
	item: T;
};

export type SelectHeader = {
	type: "header";
	text: string;
};

export type SelectGroup<T extends AcceptableValue> = {
	type: "group";
	label: string;
	items: SelectItem<T>[];
};

export type SelectElement<T extends AcceptableValue> =
	| SelectItem<T>
	| SelectHeader
	| SelectGroup<T>;

export type SelectItemProps<T extends AcceptableValue> = {
	items: T[];
	itemText?: keyof T & string;
	itemValue?: keyof T & string;
	itemDisabled?: (item: T) => boolean;
};

export function useItems<T extends AcceptableValue>(props: Omit<SelectItemProps<T>, "items">) {
	function getItemValue(item: T | NotDefined) {
		if (item == null) return;

		if (typeof item !== "object") {
			return item;
		} else {
			return item[props.itemValue || "value"];
		}
	}

	function getItemText(item: T | NotDefined) {
		if (item == null) return;

		if (typeof item !== "object") {
			return item.toString();
		} else {
			return item[props.itemText || "text"];
		}
	}

	function getItemDisabled(item: T | NotDefined) {
		if (item == null) return false;

		if (props.itemDisabled) {
			return props.itemDisabled(item);
		}

		if (typeof item === "object" && "disabled" in item) {
			return !!item.disabled;
		}

		return false;
	}

	function makeInternalDisplayItem(item: T): SelectItem<T> {
		return {
			type: "item",
			value: getItemValue(item),
			text: getItemText(item),
			disabled: getItemDisabled(item),
			item,
		};
	}

	function makeNewItem(item: string): SelectItem<T> {
		let customItem: Record<string, string> | string = {};
		if (props.itemText) {
			customItem[props.itemText as string] = item;
		}
		if (props.itemValue) {
			customItem[props.itemValue as string] = item;
		}

		if (Object.keys(customItem).length === 0) {
			customItem = item;
		}

		return {
			type: "item",
			value: item,
			text: item.toString(),
			disabled: false,
			item: customItem as any,
		};
	}

	function makeItems(items: T[]): SelectItem<T>[] {
		return items.map((item) => makeInternalDisplayItem(item));
	}

	return {
		getItemDisabled,
		getItemText,
		getItemValue,
		makeItems,
		makeNewItem,
	};
}
